/* eslint-disable react-hooks/exhaustive-deps */
import { useAuthState } from '@/app/providers/auth'
import type { UpdateBasketItemPayload } from '@/components/page-specific/catering/common/components/states/menu-page/update-item-mutation'
import {
  generateBasketLink,
  generateCateringTeamOrderLink,
  generateOrderLink,
  generateStandingOrderLink,
  getBasketIdParam,
  isCateringTeamOrderShareVisit,
} from '@/components/page-specific/catering/common/helpers/basket-link'
import {
  AddressErrors,
  DateErrors,
} from '@/components/page-specific/catering/common/types/enums/Errors.enum'
import type { BasketFragment, MenuVendorFragment } from '@/generated/graphql'
import { Language } from '@/generated/graphql'
import { useCateringTeamOrderEnabled } from '@/helpers/features/cateringTeamOrder'
import { basketLocationToAddress } from '@caterdesk/utils--gm-validation'

import { isAfter, isBefore, isEqual, parseISO, startOfDay } from 'date-fns'
import { useSearchParams, useRouter } from 'next/navigation'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { match } from 'ts-pattern'
import { useShallow } from 'zustand/react/shallow'

import { validateUserBasketSettings } from '../../../common/domain/user-basket-settings'
import type {
  UserBasketSettings,
  ValidUserBasketSettings,
} from '../../../common/domain/user-basket-settings'
import { useUserBasketSettingsState } from '../../../common/states/useUserBasketSettingsState'
import {
  useAppStateErrors,
  useAppStateStore,
  useUserStateActions,
} from '../../../common/store/appStateStore'
import {
  buildContributorContext,
  isBasketContributor,
} from '../../domain/catering-team-order-viewer'
import { useAddressInRangeCheck } from '../../hooks/useAddressInRangeCheck'
import { useMixpanelBasketCreated } from '../../hooks/useMixpanelBasketCreated'
import { useMixpanelItemAddedToBasket } from '../../hooks/useMixpanelItemAddedToBasket'
import { useBasketSelection } from '../../states/basket-selection/useBasketSelection'
import { useSelectedBasketState } from '../../states/selected-basket'
import { useExistingOrder } from '../../states/useExistingOrder'
import { useBasketStateStore } from '../../store/basketStateStore'
import { useCartStore, useCartModals } from '../../store/cartStore'
import { useVendorLocationData } from '../../store/vendorLocationStore'
import { useMappedBasketOrOrderDataToCartData } from '../consolidated-basket/useMappedBasketOrOrderDataToCartData'
import { formatFullAddress } from '../input-bar/useAddressLabel'

const MAX_NUMBER_OF_DRAFT_BASKETS = 4

interface UseCartLogicProps {
  vendor: MenuVendorFragment | null
}

type BasketItem = BasketFragment['items'][number]

const parseToStartOfDay = (value?: string | null) => {
  if (!value) return null
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return null
  return startOfDay(parsed)
}

type ActiveWindowItem = {
  isActive?: boolean | null
  activeFrom?: string | null
  activeTo?: string | null
} | null

const isMenuItemActiveForDate = (item: ActiveWindowItem, deliveryDate?: number) => {
  if (!deliveryDate) return true
  if (!item) return true

  const date = startOfDay(new Date(deliveryDate))
  if (Number.isNaN(date.getTime())) return true

  const activeFrom = parseToStartOfDay(item.activeFrom)
  const activeTo = parseToStartOfDay(item.activeTo)

  if (item.isActive === false) return false
  if (activeFrom && isEqual(date, activeFrom)) return true
  if (activeTo && isEqual(date, activeTo)) return true

  if (activeFrom && activeTo) {
    return (
      (isAfter(date, activeFrom) || isEqual(date, activeFrom)) &&
      (isBefore(date, activeTo) || isEqual(date, activeTo))
    )
  }

  if (activeFrom) {
    return isAfter(date, activeFrom) || isEqual(date, activeFrom)
  }

  if (activeTo) {
    return isBefore(date, activeTo) || isEqual(date, activeTo)
  }

  return true
}

const isBasketItemAvailableForDate = (basketItem: BasketItem, deliveryDate?: number) =>
  isMenuItemActiveForDate(basketItem?.item, deliveryDate) &&
  (basketItem?.options ?? []).every((option) =>
    isMenuItemActiveForDate(option?.item ?? null, deliveryDate),
  )

export const useShoppingCart = ({ vendor }: UseCartLogicProps) => {
  const searchParams = useSearchParams()
  const router = useRouter()
  const authState = useAuthState()
  const { updateUserState } = useUserStateActions()
  const { setErrors } = useAppStateErrors()
  const latestRangeCheckRef = useRef(0)
  const { validateLocationInRange } = useAddressInRangeCheck({ vendorId: vendor?.id })
  const { trackBasketCreated } = useMixpanelBasketCreated()

  const { addingItem: sharedAddingItem, updatingItem: sharedUpdatingItem } = useBasketStateStore()
  const cateringTeamOrderEnabled = useCateringTeamOrderEnabled()
  // Latched in state rather than derived: basketSelectionConfig below is computed before the
  // hooks that load basketData run, so it can only see the previous render's resolution.
  const [resolvedViewerRole, setResolvedViewerRole] = useState<'unknown' | 'own' | 'contributor'>(
    'unknown',
  )
  const setAddressError = useCallback(
    (message: string | null) => {
      const currentErrors = useAppStateStore.getState().errors
      const nextErrors = { ...currentErrors }
      if (message) {
        nextErrors.address = message
      } else {
        delete nextErrors.address
      }
      setErrors(nextErrors)
    },
    [setErrors],
  )

  // Extract URL parameters
  const orderIdParam = searchParams?.get('orderId') || ''
  const standingOrderIdParam = searchParams?.get('standingOrderId') || ''
  const basketIdParam = getBasketIdParam(searchParams)
  const preventAutomaticBasketSelection = Boolean(searchParams?.get('preventAutoBasketSelection'))
  const isCateringTeamOrderUrlParam = isCateringTeamOrderShareVisit(searchParams)

  // Unauthenticated share visitors belong to the page shell (useCateringTeamOrderEntry), which redirects
  // them or reports the link dead; nothing is fetched for them here, and 'loading' counts so the
  // by-id fetch can't fire before auth settles.
  const isUnauthenticatedShareVisit =
    cateringTeamOrderEnabled && isCateringTeamOrderUrlParam && authState.type !== 'authenticated'

  // Cart store
  const {
    orderDetailsModal: orderDetailsModalState,
    editItemModal,
    pendingCateringTeamOrder,
  } = useCartModals()
  const {
    setOrderDetailsModalState,
    setPendingCateringTeamOrder,
    setEditItemModal,
    setPreviewData,
  } = useCartStore(
    useShallow((state) => ({
      setOrderDetailsModalState: state.setOrderDetailsModalState,
      setPendingCateringTeamOrder: state.setPendingCateringTeamOrder,
      setEditItemModal: state.setEditItemModal,
      setPreviewData: state.setPreviewData,
    })),
  )
  const showLinkCopiedSnackbar = useCartStore((state) => state.showLinkCopiedSnackbar)

  const isEditingExistingOrder = Boolean(orderIdParam)
  const isEditingStandingOrder = Boolean(standingOrderIdParam)

  const {
    selectedVendorLocation,
    deliveryFee,
    minOrderValueExTax,
    taxAcronym,
    availableVendorLocations,
  } = useVendorLocationData()

  // Configure basket selection based on current context
  const basketSelectionConfig = useMemo(
    () => ({
      isAuthenticatedUser: authState.type === 'authenticated',
      accountId:
        authState.type === 'authenticated' ? (authState.user.account?.id ?? undefined) : undefined,
      vendorId:
        authState.type !== 'loading' &&
        !isEditingExistingOrder &&
        !isEditingStandingOrder &&
        // Contributors never see other baskets: an unset vendorId stops the viewer's own draft
        // list being fetched. The URL term covers the window before the basket has resolved —
        // gated, because with team ordering off the basket resolves not-found so the role
        // never settles and an ungated term would hide the viewer's drafts for the whole visit.
        resolvedViewerRole !== 'contributor' &&
        (!(cateringTeamOrderEnabled && isCateringTeamOrderUrlParam && basketIdParam) ||
          resolvedViewerRole === 'own') &&
        !isUnauthenticatedShareVisit &&
        vendor?.id
          ? vendor.id
          : undefined,
      // Suppressed like vendorId while the join redirect is pending — nothing should be
      // fetched for a visitor who is about to be sent to login.
      initialSelectedId: isUnauthenticatedShareVisit ? undefined : basketIdParam,
      preventAutomaticSelection: preventAutomaticBasketSelection,
      isCateringTeamOrderUrl: isCateringTeamOrderUrlParam,
    }),
    [
      authState.type,
      authState.type === 'authenticated' ? authState.user.account?.id : undefined,
      isEditingExistingOrder,
      isEditingStandingOrder,
      resolvedViewerRole,
      cateringTeamOrderEnabled,
      isCateringTeamOrderUrlParam,
      isUnauthenticatedShareVisit,
      vendor?.id,
      basketIdParam,
      preventAutomaticBasketSelection,
    ],
  )

  const {
    selectedBasket,
    unselectedBaskets,
    setSelectedBasket,
    deleteBasket: originalDeleteBasket,
    createBasket,
    ...basketSelectionState
  } = useBasketSelection(basketSelectionConfig)

  const deleteBasket = originalDeleteBasket

  const { set: setUserBasketSettings, value: userBasketSettingsValue } =
    useUserBasketSettingsState()

  const { trackItemAddedToBasket } = useMixpanelItemAddedToBasket({
    vendor,
    orderSettings: userBasketSettingsValue,
  })

  const { basketDesignedUiCompatibleOrderData, ...existingOrderState } = useExistingOrder()

  const { recalculatingFees, recalculatingTotals, orderDataCalled } = existingOrderState

  const {
    basket: basketData,
    minimumOrderReached: basketMinimumOrderReached,
    minSpendRemaining: basketMinSpendRemaining,
    ...basketState
  } = useSelectedBasketState({
    basketId: selectedBasket?.id ?? null,
    language: Language.En,
    standingOrderId: standingOrderIdParam,
    deliveryFee,
    minOrderValueExTax,
    onBasketDataChange: useCallback(
      (newBasketData: BasketFragment) => {
        if (isEditingExistingOrder || isEditingStandingOrder) return
        const currentLocation = useAppStateStore.getState().userState.location
        if (currentLocation?.__typename === 'AccountLocation') {
          setUserBasketSettings({
            date: parseISO(newBasketData.settings.deliveryDate).getTime(),
            time: newBasketData.settings.deliveryTimeSlot,
            headCount: newBasketData.settings.headCount,
          })
          return
        }
        setUserBasketSettings({
          location: newBasketData.settings.location || undefined,
          date: parseISO(newBasketData.settings.deliveryDate).getTime(),
          time: newBasketData.settings.deliveryTimeSlot,
          headCount: newBasketData.settings.headCount,
        })
      },
      [setUserBasketSettings, isEditingExistingOrder, isEditingStandingOrder],
    ),
  })

  // Gated so switching team ordering off is a clean visual kill-switch; selectedBasket
  // covers the fetch window after switching baskets, when basketData is still null.
  const isCateringTeamOrderBasket =
    cateringTeamOrderEnabled &&
    Boolean(
      isEditingExistingOrder
        ? existingOrderState.orderData?.isCateringTeamOrder
        : (basketData?.isCateringTeamOrder ?? selectedBasket?.isCateringTeamOrder),
    )

  const isCateringTeamOrderContributor =
    cateringTeamOrderEnabled &&
    isBasketContributor(
      basketData,
      authState.type === 'authenticated'
        ? { id: authState.user.id, accountId: authState.user.account?.id }
        : null,
    )

  const hasBasketData = Boolean(basketData)
  useEffect(() => {
    setResolvedViewerRole(
      hasBasketData ? (isCateringTeamOrderContributor ? 'contributor' : 'own') : 'unknown',
    )
  }, [hasBasketData, isCateringTeamOrderContributor])

  // The invite banner always shows the whole address; the gated short label the input bar
  // uses would leave a contributor with just a postcode.
  const cateringTeamOrderLocationLabel = basketData?.settings.location
    ? formatFullAddress(basketLocationToAddress(basketData.settings.location))
    : ''
  const cateringTeamOrderContext = useMemo(
    () =>
      isCateringTeamOrderContributor && basketData
        ? buildContributorContext(basketData, cateringTeamOrderLocationLabel)
        : null,
    [isCateringTeamOrderContributor, basketData, cateringTeamOrderLocationLabel],
  )

  const orderSelectedSettings = match<
    { isEditingStandingOrder: boolean; isEditingExistingOrder: boolean },
    UserBasketSettings
  >({ isEditingExistingOrder, isEditingStandingOrder })
    .with({ isEditingExistingOrder: true }, () => ({ ...basketDesignedUiCompatibleOrderData }))
    .with({ isEditingStandingOrder: true }, () => ({
      ...basketData,
      location: basketData?.settings.location || undefined,
      date: basketData ? new Date(basketData?.settings.deliveryDate).getTime() : undefined,
      time: basketData?.settings.deliveryTimeSlot,
      headCount: basketData?.settings.headCount,
      weekdays: basketData?.standingOrderSettings?.daysOfOccurrence ?? [],
    }))
    .otherwise(() => userBasketSettingsValue)

  useEffect(() => {
    if (!vendor?.id) return
    if (!orderSelectedSettings.location) {
      setAddressError(null)
      return
    }

    const requestId = ++latestRangeCheckRef.current

    validateLocationInRange(orderSelectedSettings.location, {
      headCount: orderSelectedSettings.headCount ?? null,
    })
      .then((result) => {
        if (requestId !== latestRangeCheckRef.current) return
        setAddressError(result.ok ? null : AddressErrors.VENDER_COVERAGE)
      })
      .catch(() => {
        if (requestId !== latestRangeCheckRef.current) return
        setAddressError(AddressErrors.VENDER_COVERAGE)
      })
  }, [
    orderSelectedSettings.headCount,
    orderSelectedSettings.location,
    setAddressError,
    validateLocationInRange,
    vendor?.id,
  ])

  const editingStandingOrderTemplate = Boolean(basketData?.standingOrderSettings)

  const mappedBasketorOrderDataToCartData = useMappedBasketOrOrderDataToCartData({
    orderData: {
      rawData: existingOrderState.orderData,
      fees: existingOrderState.fees,
      taxRateTotals: existingOrderState.taxRateTotals,
      grandTotals: existingOrderState.grandTotals,
      itemsState: existingOrderState.orderItems,
    },
    basketData,
    standingOrderData: basketData?.standingOrderSettings ? basketData.standingOrderSettings : null,
    cateringTeamOrder: {
      isCateringTeamOrder: isCateringTeamOrderBasket,
      currentUserId: authState.type === 'authenticated' ? String(authState.user.id) : undefined,
      isContributor: isCateringTeamOrderContributor,
    },
  })

  const selectedDeliveryTimestamp = useMemo(() => {
    if (orderSelectedSettings.date) return orderSelectedSettings.date
    if (userBasketSettingsValue.date) return userBasketSettingsValue.date

    if (basketData?.settings.deliveryDate) {
      const parsedDeliveryDate = parseISO(basketData.settings.deliveryDate)
      if (!Number.isNaN(parsedDeliveryDate.getTime())) {
        return parsedDeliveryDate.getTime()
      }
    }

    return undefined
  }, [orderSelectedSettings.date, userBasketSettingsValue.date, basketData?.settings.deliveryDate])

  const isDeliveryDatePast = useMemo(() => {
    if (!selectedDeliveryTimestamp) return false
    if (isEditingExistingOrder || isEditingStandingOrder) return false
    return isBefore(startOfDay(new Date(selectedDeliveryTimestamp)), startOfDay(new Date()))
  }, [selectedDeliveryTimestamp, isEditingExistingOrder, isEditingStandingOrder])

  useEffect(() => {
    const currentErrors = useAppStateStore.getState().errors
    const isFlaggedPast = currentErrors.date === DateErrors.DATE_PAST
    if (isDeliveryDatePast && !isFlaggedPast) {
      setErrors({ ...currentErrors, date: DateErrors.DATE_PAST })
    } else if (!isDeliveryDatePast && isFlaggedPast) {
      const { date, ...rest } = currentErrors
      setErrors(rest)
    }
  }, [isDeliveryDatePast, setErrors])

  const unavailableBasketItems = useMemo(() => {
    if (!basketData || !selectedDeliveryTimestamp) return []

    return basketData.items.filter(
      (basketItem) => !isBasketItemAvailableForDate(basketItem, selectedDeliveryTimestamp),
    )
  }, [basketData, selectedDeliveryTimestamp])

  const handleRemoveUnavailableItems = useCallback(() => {
    if (!basketData || unavailableBasketItems.length === 0) return

    unavailableBasketItems.forEach((basketItem) => {
      basketState.removeItemFromBasket(basketData, {
        itemReference: basketItem.itemReference,
      })
    })
  }, [basketData, unavailableBasketItems, basketState.removeItemFromBasket])

  const isInitialLoad =
    authState.type === 'loading' ||
    match({ isEditingExistingOrder, isEditingStandingOrder })
      .with({ isEditingExistingOrder: true }, () => !orderDataCalled)
      .with({ isEditingStandingOrder: true }, () => !basketState.called)
      .otherwise(() => {
        return (
          !basketSelectionState.called ||
          Boolean(!basketSelectionState.justCreated && selectedBasket?.id && !basketState.called)
        )
      })

  const loadingOrderData =
    isInitialLoad ||
    match(isEditingExistingOrder)
      .with(true, () => Boolean(existingOrderState.loadingOrderData))
      .otherwise(() => false)

  const loadingStandingOrderData =
    isInitialLoad ||
    match(isEditingStandingOrder)
      .with(true, () => {
        return Boolean(basketState.loading)
      })
      .otherwise(() => false)

  const loadingBasketData =
    isInitialLoad ||
    match(isEditingExistingOrder)
      .with(true, () => false)
      .otherwise(
        () => basketState.loading || (!basketData && Boolean(basketSelectionState.loading)),
      )

  const loadingBasketDrawerItems = loadingOrderData || loadingBasketData || loadingStandingOrderData
  const isDataUpdating =
    recalculatingFees ||
    recalculatingTotals ||
    basketState.updating ||
    loadingBasketData ||
    sharedAddingItem ||
    sharedUpdatingItem

  const vendorUpdatedItems = useMemo(() => {
    return mappedBasketorOrderDataToCartData.items.filter((item) => item.updatedByVendor)
  }, [mappedBasketorOrderDataToCartData.items])

  // Business Logic Functions
  const handleCreateBasket = useCallback(
    (options?: { isCateringTeamOrder?: boolean }) => {
      const isCateringTeamOrder = options?.isCateringTeamOrder ?? false
      // Persisted in the store (not a local var) so the flag survives into
      // handleUserBasketSettingsConfirm when settings are invalid and creation defers
      // to the order-details modal below.
      setPendingCateringTeamOrder(isCateringTeamOrder)

      const account = authState.type === 'authenticated' ? authState.user.account : null
      const validatedBasketSettings = validateUserBasketSettings(userBasketSettingsValue)
      const validated = true
      const availabilityId = vendor?.gmAvailability?.id
      if (!selectedVendorLocation?.id) {
        setAddressError(AddressErrors.VENDER_COVERAGE)
        setOrderDetailsModalState({
          open: true,
          createOnConfirm: true,
        })
        return
      }

      if (!validated || validatedBasketSettings.type !== 'success' || !availabilityId) {
        setOrderDetailsModalState({
          open: true,
          createOnConfirm: true,
        })
        return
      }

      createBasket({
        vendor: vendor as unknown as any,
        vendorLocationId: selectedVendorLocation.id,
        account,
        availabilityId,
        userBasketSettings: validatedBasketSettings.value,
        isCateringTeamOrder,
        onBasketCreated: () => {
          setPendingCateringTeamOrder(false)
          if (vendor) {
            trackBasketCreated({
              vendor: {
                id: vendor.id,
                companyName: vendor.companyName,
                isPantry: vendor.isPantry,
              },
              orderSettings: userBasketSettingsValue,
              isCateringTeamOrder,
            })
          }
        },
      })
    },
    [
      authState,
      userBasketSettingsValue,
      vendor,
      createBasket,
      setOrderDetailsModalState,
      setPendingCateringTeamOrder,
      setAddressError,
      selectedVendorLocation,
      trackBasketCreated,
    ],
  )

  // Wraps the order-details modal's own open/close (its X button, not the Confirm flow —
  // that's handleUserBasketSettingsConfirm) so a team-order arm from the toolbar doesn't
  // leak into a later, unrelated basket creation if the user cancels out.
  const handleOrderDetailsModalOpenChange = useCallback(
    (open: boolean) => {
      setOrderDetailsModalState({ open })
      if (!open) setPendingCateringTeamOrder(false)
    },
    [setOrderDetailsModalState, setPendingCateringTeamOrder],
  )

  const handleSaveBasketName = useCallback(
    (newName: string) => {
      if (!basketData) return
      basketState.updateBasket({
        basket: Object.assign(basketData, { customName: newName }),
      })
    },
    [basketData, basketState.updateBasket], // eslint-disable-line react-hooks/exhaustive-deps
  )

  const handleUserBasketSettingsConfirm = useCallback(
    (userBasketSettings: ValidUserBasketSettings) => {
      if (orderDetailsModalState.open && orderDetailsModalState.createOnConfirm) {
        // Create basket with the item that was queued to be added
        const account = authState.type === 'authenticated' ? authState.user.account : null
        const availabilityId = vendor?.gmAvailability?.id

        if (!availabilityId) {
          setOrderDetailsModalState({ open: false })
          setPendingCateringTeamOrder(false)
          return
        }

        const itemToAdd = orderDetailsModalState.itemToAddOnCreate
        const itemAddSource = orderDetailsModalState.itemToAddSource

        // Close the modal immediately
        setOrderDetailsModalState({ open: false })

        return createBasket({
          vendor: vendor as unknown as any,
          vendorLocationId: selectedVendorLocation?.id,
          account,
          availabilityId,
          itemPayload: itemToAdd,
          userBasketSettings,
          isCateringTeamOrder: pendingCateringTeamOrder,
          onBasketCreated: () => {
            setPendingCateringTeamOrder(false)
            if (vendor) {
              trackBasketCreated({
                vendor: {
                  id: vendor.id,
                  companyName: vendor.companyName,
                  isPantry: vendor.isPantry,
                },
                orderSettings: userBasketSettings,
                isCateringTeamOrder: pendingCateringTeamOrder,
              })
            }
            if (itemToAdd?.item) {
              trackItemAddedToBasket({
                itemId: itemToAdd.item._id,
                itemName: itemToAdd.item.name,
                quantity: itemToAdd.qty,
                source: itemAddSource ?? 'SupplierMenu',
                isCateringTeamOrder: pendingCateringTeamOrder,
              })
            }
          },
        })
      }
      if (basketData) {
        const account = authState.type === 'authenticated' ? authState.user.account : null

        return basketState.updateBasket({
          basket: basketData,
          selectedVendorLocation: selectedVendorLocation || null,
          vendor: vendor as unknown as any,
          account,
          userBasketSettings,
          onCompleted: () => {
            setOrderDetailsModalState({ open: false })
          },
        })
      }
      setOrderDetailsModalState({ open: false })
    },
    [
      orderDetailsModalState,
      basketData,
      authState,
      basketState.updateBasket,
      vendor,
      createBasket,
      setOrderDetailsModalState,
      pendingCateringTeamOrder,
      setPendingCateringTeamOrder,
      selectedVendorLocation,
      trackBasketCreated,
      trackItemAddedToBasket,
    ],
  )

  const checkoutCallback = useCallback(
    async (basketId: string) => {
      const vendorLocationId = basketData?.settings?.vendor?.locationId
      if (!vendorLocationId) {
        setOrderDetailsModalState({ open: true })
        return
      }
      const usersValidatedState = validateUserBasketSettings(userBasketSettingsValue)
      if (usersValidatedState.type === 'success') {
        try {
          await handleUserBasketSettingsConfirm(usersValidatedState.value)
        } catch {
          return
        }
        router.push(`/checkout/?id=${basketId}`)
      } else {
        setOrderDetailsModalState({ open: true })
      }
    },
    [
      userBasketSettingsValue,
      handleUserBasketSettingsConfirm,
      setOrderDetailsModalState,
      router,
      selectedVendorLocation,
      setAddressError,
    ],
  )

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleDeleteItem = useCallback(
    (itemId: string) => {
      if (basketData) {
        basketState.removeItemFromBasket(basketData, {
          itemReference: itemId,
        })
      } else if (isEditingExistingOrder) {
        existingOrderState.removeItemFromOrder({ id: itemId })
      }
    },
    [
      basketData,
      basketState.removeItemFromBasket,
      isEditingExistingOrder,
      existingOrderState.removeItemFromOrder,
    ], // eslint-disable-line react-hooks/exhaustive-deps
  )

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleQuantityChange = useCallback(
    (itemId: string, qty: number) => {
      if (basketData) {
        basketState.updateItemQty(basketData, { itemReference: itemId }, qty)
      } else if (isEditingExistingOrder) {
        existingOrderState.adjustItemQuantity({
          id: itemId,
          newQty: qty,
        })
      }
    },
    [
      basketData,
      basketState.updateItemQty,
      isEditingExistingOrder,
      existingOrderState.adjustItemQuantity,
    ], // eslint-disable-line react-hooks/exhaustive-deps
  )

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleNoteChange = useCallback(
    (itemId: string, note: string) => {
      if (basketData) {
        basketState.updateItemInBasket(basketData, { itemReference: itemId }, { note })
      }
    },
    [basketData, basketState.updateItemInBasket], // eslint-disable-line react-hooks/exhaustive-deps
  )

  const handleNumberedOptionQuantityChange = useCallback(
    (coreItemId: string) => {
      setEditItemModal({
        type: 'displayed',
        menuItemReference: coreItemId,
        mode: 'add',
      })
    },
    [setEditItemModal],
  )

  const handleCopyFriendlyId = useCallback(
    (basketId?: string) => {
      const idToCopy = basketId || mappedBasketorOrderDataToCartData.id || selectedBasket?.id || ''

      // Resolve group-ness of the basket actually being copied: collapsed-row copies pass
      // their basketId (looked up in the stack), the active header passes none (use the
      // loaded basket). Keying off the active basket alone would enrich/bare the wrong
      // link when copying a collapsed row while a different basket is selected.
      const targetIsCateringTeamOrder = basketId
        ? Boolean(unselectedBaskets.find((basket) => basket.id === basketId)?.isCateringTeamOrder)
        : Boolean(basketData?.isCateringTeamOrder)

      const linkToCopy = match({
        isEditingExistingOrder,
        isEditingStandingOrder,
      })
        .with({ isEditingExistingOrder: true }, () =>
          generateOrderLink(mappedBasketorOrderDataToCartData.id),
        )
        .with({ isEditingStandingOrder: true }, () =>
          generateStandingOrderLink(mappedBasketorOrderDataToCartData.id),
        )
        .otherwise(() =>
          targetIsCateringTeamOrder
            ? generateCateringTeamOrderLink(
                idToCopy,
                authState.type === 'authenticated' ? authState.user.account : null,
              )
            : generateBasketLink(idToCopy),
        )

      navigator.clipboard.writeText(linkToCopy)
      showLinkCopiedSnackbar()
    },
    [
      authState,
      basketData?.isCateringTeamOrder,
      isEditingExistingOrder,
      isEditingStandingOrder,
      mappedBasketorOrderDataToCartData.id,
      selectedBasket?.id,
      showLinkCopiedSnackbar,
      unselectedBaskets,
    ],
  )

  const handleVendorUpdatedItemsKeep = useCallback(() => {
    if (basketData) {
      vendorUpdatedItems.map((item) =>
        basketState.updateItemInBasket(
          basketData,
          { itemReference: item.id },
          {
            acknowledgedAt: new Date(),
          },
        ),
      )
    }
  }, [basketData, vendorUpdatedItems, basketState.updateItemInBasket]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleVendorUpdatedItemsRemove = useCallback(() => {
    if (basketData) {
      vendorUpdatedItems.map((item) =>
        basketState.removeItemFromBasket(basketData, {
          itemReference: item.id,
        }),
      )
    }
  }, [basketData, vendorUpdatedItems, basketState.removeItemFromBasket]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleEditItemConfirm = useCallback(
    ({ qty, options }: UpdateBasketItemPayload) => {
      if (!basketData || editItemModal.type !== 'displayed') return
      const basketInput = { qty, options }
      basketState.updateItemInBasket(basketData, editItemModal.basketItem, basketInput)
    },
    [editItemModal, basketData, basketState.updateItemInBasket], // eslint-disable-line react-hooks/exhaustive-deps
  )

  useEffect(() => {
    setPreviewData({
      itemQuantity: mappedBasketorOrderDataToCartData.summary.itemCount,
      grandTotal: mappedBasketorOrderDataToCartData.summary.grandTotal,
      newItemWasJustAdded: basketState.newItemJustAdded || existingOrderState.newItemWasJustAdded,
    })
  }, [
    mappedBasketorOrderDataToCartData.summary.grandTotal,
    mappedBasketorOrderDataToCartData.summary.itemCount,
    basketState.newItemJustAdded,
    existingOrderState.newItemWasJustAdded,
    setPreviewData,
  ])

  // No basket to create into while editing an order; contributors never own drafts.
  const showNewBasketActions =
    unselectedBaskets.length < MAX_NUMBER_OF_DRAFT_BASKETS &&
    !isCateringTeamOrderContributor &&
    !isEditingExistingOrder &&
    !isEditingStandingOrder

  return {
    // State
    orderSelectedSettings,
    showNewBasketActions,
    editingStandingOrderTemplate,
    mappedBasketorOrderDataToCartData,
    loadingBasketDrawerItems,
    isDataUpdating: isDataUpdating,
    vendorUpdatedItems,
    basketMinimumOrderReached,
    basketMinSpendRemaining,
    selectedBasket,
    unselectedBaskets,
    basketData,
    unavailableBasketItems,
    isDeliveryDatePast,
    taxAcronym,
    availableVendorLocations,
    existingOrderState,
    basketState,
    isEditingExistingOrder,
    isEditingStandingOrder,
    isCateringTeamOrderBasket,
    isCateringTeamOrderContributor,
    cateringTeamOrderContext,
    deliveryFee,
    // Expose shared loading states
    addingItem: sharedAddingItem,
    updatingItem: sharedUpdatingItem,

    // Actions
    handleCreateBasket,
    handleOrderDetailsModalOpenChange,
    handleSaveBasketName,
    handleUserBasketSettingsConfirm,
    checkoutCallback,
    handleDeleteItem,
    handleQuantityChange,
    handleNoteChange,
    handleNumberedOptionQuantityChange,
    handleCopyFriendlyId,
    handleVendorUpdatedItemsKeep,
    handleVendorUpdatedItemsRemove,
    handleRemoveUnavailableItems,
    handleEditItemConfirm,
    deleteBasket,
    setSelectedBasket,
    updateUserState,
  }
}
